import { filter, includes, map, orderBy, size, trim } from 'lodash';

export const REVIEW_MEDIA_TYPES = {
  IMAGE: 'IMAGE',
  VIDEO: 'VIDEO',
};

export const REVIEW_CHANGE_STATUS = {
  ADDED: 'ADDED',
  REMOVED: 'REMOVED',
  UNCHANGED: 'UNCHANGED',
};

export const REVIEW_COMPARE_FIELDS = {
  RATING: 'RATING',
  CONTENT: 'CONTENT',
  TAGS: 'TAGS',
  MEDIA: 'MEDIA',
  ENGAGEMENT_DURATION: 'ENGAGEMENT_DURATION',
  AGE_GROUP: 'AGE_GROUP',
  IS_AI_GENERATED: 'IS_AI_GENERATED',
};

export const REVIEW_COMPARE_FIELD_LABELS = {
  RATING: 'Rating',
  CONTENT: 'Review text',
  TAGS: 'Tags',
  MEDIA: 'Media',
  ENGAGEMENT_DURATION: 'Play time',
  AGE_GROUP: 'Age group',
  IS_AI_GENERATED: 'AI generated',
};

// Upper bound of the word diff matrix, longer texts are compared as a whole.
const MAX_DIFF_CELLS = 250000;

export const getMediaId = (media) => media?.key || media?.url || '';

export const getTagId = (tag) => tag?.id || tag?.itemKey || tag?.label || '';

export const sortByOrder = (list) =>
  orderBy(list || [], [(item) => item?.order ?? 0], ['asc']);

export const isVideoMedia = (media) =>
  media?.type === REVIEW_MEDIA_TYPES?.VIDEO ||
  includes(media?.mimeType, 'video');

export const formatAgeGroup = (ageGroup) => {
  const minAge = ageGroup?.minAge;
  const maxAge = ageGroup?.maxAge;

  if (minAge == null && maxAge == null) {
    return '';
  }

  if (!Number.isFinite(minAge) || !Number.isFinite(maxAge)) {
    return '';
  }

  return `${Math.floor(minAge / 12)} - ${Math.floor(maxAge / 12)} Years`;
};

// Backend sends either a readable string or an enum like LESS_THAN_30_MIN.
export const formatEngagementDuration = (value) => {
  if (!value) {
    return '';
  }
  if (/^[A-Z0-9_]+$/.test(value)) {
    const label = value.replace(/_/g, ' ').toLowerCase();
    return `${label.charAt(0).toUpperCase()}${label.slice(1)}`;
  }
  return value;
};

export const formatRating = (rating) =>
  rating || rating === 0 ? `${rating}` : '';

const tokenizeText = (text) =>
  filter((text || '').split(/(\s+)/), (token) => token !== '');

const pushToken = (list, value, type) => {
  const lastToken = list?.[list?.length - 1];
  if (lastToken?.type === type) {
    lastToken.value += value;
    return;
  }
  list?.push({ value, type });
};

/**
 * Word level diff of the previous and the updated review text so that a
 * reviewer can spot the exact edit instead of re-reading both versions.
 */
export const getWordLevelDiff = (previousText, updatedText) => {
  const previousTokens = tokenizeText(previousText);
  const updatedTokens = tokenizeText(updatedText);
  const rows = size(previousTokens);
  const cols = size(updatedTokens);

  if (!rows || !cols || rows * cols > MAX_DIFF_CELLS) {
    return {
      previous: rows
        ? [{ value: previousText, type: REVIEW_CHANGE_STATUS?.REMOVED }]
        : [],
      updated: cols
        ? [{ value: updatedText, type: REVIEW_CHANGE_STATUS?.ADDED }]
        : [],
    };
  }

  // Longest common subsequence lengths, table[i][j] = LCS of tail(i), tail(j).
  const table = [];
  for (let row = 0; row <= rows; row += 1) {
    table?.push(new Array(cols + 1)?.fill(0));
  }
  for (let row = rows - 1; row >= 0; row -= 1) {
    for (let col = cols - 1; col >= 0; col -= 1) {
      table[row][col] =
        previousTokens?.[row] === updatedTokens?.[col]
          ? table?.[row + 1]?.[col + 1] + 1
          : Math?.max(table?.[row + 1]?.[col], table?.[row]?.[col + 1]);
    }
  }

  const previous = [];
  const updated = [];
  let row = 0;
  let col = 0;
  while (row < rows && col < cols) {
    if (previousTokens?.[row] === updatedTokens?.[col]) {
      pushToken(
        previous,
        previousTokens?.[row],
        REVIEW_CHANGE_STATUS?.UNCHANGED,
      );
      pushToken(
        updated,
        updatedTokens?.[col],
        REVIEW_CHANGE_STATUS?.UNCHANGED,
      );
      row += 1;
      col += 1;
    } else if (
      table?.[row + 1]?.[col] >= table?.[row]?.[col + 1]
    ) {
      pushToken(
        previous,
        previousTokens?.[row],
        REVIEW_CHANGE_STATUS?.REMOVED,
      );
      row += 1;
    } else {
      pushToken(
        updated,
        updatedTokens?.[col],
        REVIEW_CHANGE_STATUS?.ADDED,
      );
      col += 1;
    }
  }
  while (row < rows) {
    pushToken(
      previous,
      previousTokens?.[row],
      REVIEW_CHANGE_STATUS?.REMOVED,
    );
    row += 1;
  }
  while (col < cols) {
    pushToken(
      updated,
      updatedTokens?.[col],
      REVIEW_CHANGE_STATUS?.ADDED,
    );
    col += 1;
  }

  return { previous, updated };
};

const getListDiff = (previousList, updatedList, getId) => {
  const previous = sortByOrder(previousList);
  const updated = sortByOrder(updatedList);
  const previousIds = map(previous, getId);
  const updatedIds = map(updated, getId);

  const markedPrevious = map(previous, (item) => ({
    ...item,
    changeStatus: includes(updatedIds, getId(item))
      ? REVIEW_CHANGE_STATUS?.UNCHANGED
      : REVIEW_CHANGE_STATUS?.REMOVED,
  }));
  const markedUpdated = map(updated, (item) => ({
    ...item,
    changeStatus: includes(previousIds, getId(item))
      ? REVIEW_CHANGE_STATUS?.UNCHANGED
      : REVIEW_CHANGE_STATUS?.ADDED,
  }));

  const removedCount = size(
    filter(
      markedPrevious,
      (item) => item?.changeStatus === REVIEW_CHANGE_STATUS?.REMOVED,
    ),
  );
  const addedCount = size(
    filter(
      markedUpdated,
      (item) => item?.changeStatus === REVIEW_CHANGE_STATUS?.ADDED,
    ),
  );

  return {
    previous: markedPrevious,
    updated: markedUpdated,
    addedCount,
    removedCount,
    changed: addedCount > 0 || removedCount > 0,
  };
};

/**
 * Builds the full field by field comparison between the live review and the
 * update requested by the end user.
 */
export const getReviewComparison = (record) => {
  const updatedReview = record?.metaData || {};
  const previousReview = record?.metaData?.previousReview || {};
  const hasPreviousReview = size(record?.metaData?.previousReview) > 0;

  const rating = {
    previous: previousReview?.rating,
    updated: updatedReview?.rating,
    changed: previousReview?.rating !== updatedReview?.rating,
  };

  const content = {
    previous: previousReview?.content || '',
    updated: updatedReview?.content || '',
    changed: trim(previousReview?.content) !== trim(updatedReview?.content),
  };
  const contentDiff = content?.changed
    ? getWordLevelDiff(content?.previous, content?.updated)
    : { previous: [], updated: [] };

  const tags = getListDiff(
    previousReview?.reviewTags,
    updatedReview?.reviewTags,
    getTagId,
  );
  const media = getListDiff(
    previousReview?.media,
    updatedReview?.media,
    getMediaId,
  );

  const engagementDuration = {
    previous: previousReview?.engagementDuration || '',
    updated: updatedReview?.engagementDuration || '',
    changed:
      (previousReview?.engagementDuration || '') !==
      (updatedReview?.engagementDuration || ''),
  };

  const ageGroup = {
    previous: previousReview?.ageGroup,
    updated: updatedReview?.ageGroup,
    changed:
      formatAgeGroup(previousReview?.ageGroup) !==
      formatAgeGroup(updatedReview?.ageGroup),
  };

  const isAiGenerated = {
    previous: !!previousReview?.isAiGenerated,
    updated: !!updatedReview?.isAiGenerated,
    changed:
      !!previousReview?.isAiGenerated !== !!updatedReview?.isAiGenerated,
  };

  const changedFields = [];
  if (rating?.changed) {
    changedFields?.push(REVIEW_COMPARE_FIELDS?.RATING);
  }
  if (content?.changed) {
    changedFields?.push(REVIEW_COMPARE_FIELDS?.CONTENT);
  }
  if (tags?.changed) {
    changedFields?.push(REVIEW_COMPARE_FIELDS?.TAGS);
  }
  if (media?.changed) {
    changedFields?.push(REVIEW_COMPARE_FIELDS?.MEDIA);
  }
  if (engagementDuration?.changed) {
    changedFields?.push(REVIEW_COMPARE_FIELDS?.ENGAGEMENT_DURATION);
  }
  if (ageGroup?.changed) {
    changedFields?.push(REVIEW_COMPARE_FIELDS?.AGE_GROUP);
  }
  if (isAiGenerated?.changed) {
    changedFields?.push(REVIEW_COMPARE_FIELDS?.IS_AI_GENERATED);
  }

  return {
    hasPreviousReview,
    previousReview,
    updatedReview,
    rating,
    content,
    contentDiff,
    tags,
    media,
    engagementDuration,
    ageGroup,
    isAiGenerated,
    changedFields,
  };
};
